import { useEffect, useState } from 'react'
import useWorksCount from '@pc/components/Works/Panel/hooks/useWorksCount'
import DefaultButton from '@pc/components/Works/Panel/shared/Button/DefaultButton'
import SearchControlBox from '@pc/components/Works/Panel/shared/SearchControlBox'
import type { BigCategory, SearchParamsAttributes, SmallCategory } from '@pc/components/Works/Panel/types'
import { buildDynamicSearchApiUrl } from '@pc/components/Works/Panel/utils/workSearchApi'
import BigOccupationList from './JobSelectModal/BigOccupationList'
import PanelButtonPopupIcon from './PanelButtonPopupIcon'
import ModalCloseButton from './shared/ModalCloseButton'
import ModalFooter from './shared/ModalFooter'
import ModalHeader from './shared/ModalHeader'
import ModalInner from './shared/ModalInner'
import ModalListWrapper from './shared/ModalListWrapper'
import ModalTitle from './shared/ModalTitle'
import SelectModalDialog from './shared/SelectModalDialog'
import type { MouseEvent } from 'react'

interface PropsType {
  params: SearchParamsAttributes
}

const isKey = <T extends object>(x: T, k: PropertyKey): k is keyof T => {
  return k in x
}

const el = document.querySelector<HTMLElement>('.js-works-search-panel')

const JobSelectModal = (props: PropsType) => {
  const { params } = props
  const selectedJobCodes = params.job_codes ?? []
  const jobIds = params.job_type_ids ?? []

  const buildInitialJobsState = () => {
    const bigCategoriesAttr = el?.dataset.worksOccupationBigCategories ?? ''
    const smallCategoriesAttr = el?.dataset.worksOccupationSmallCategories ?? ''
    const bigCategories: BigCategory[] = JSON.parse(bigCategoriesAttr).data
    const smallCategories: SmallCategory[] = JSON.parse(smallCategoriesAttr).data

    const mappedSmallCategories = bigCategories.reduce((result, bigCategory) => {
      const value = smallCategories.filter((smallCategory) => smallCategory.attributes.occupation_big_category_id === bigCategory.attributes.id)
      return { ...result, [bigCategory.attributes.code]: value }
    }, {})

    return {
      bigCategories,
      mappedSmallCategories
    }
  }
  const { bigCategories, mappedSmallCategories } = buildInitialJobsState()
  const jobIdsByJobCodes = selectedJobCodes
    .map((jobCode: string) => {
      if (!isKey(mappedSmallCategories, jobCode)) return ''
      return (mappedSmallCategories[jobCode] as SmallCategory[]).map((smallCategory) => smallCategory.id)
    })
    .flat()
    .filter(Boolean)

  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [selectedJobIds, setSelectedJobIds] = useState([...new Set([...jobIds, ...jobIdsByJobCodes])])
  const { worksCount, updateCountFromParams } = useWorksCount()

  const toggleModal = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setModalIsOpen(!modalIsOpen)
  }

  const closeModal = () => {
    setModalIsOpen(false)
  }

  const changeJobIds = (jobIds: string[]) => {
    setSelectedJobIds(jobIds)
  }

  const clearState = () => {
    setSelectedJobIds([])
  }

  const mergedParams = () => {
    // 検索パネルの仕様上、jobtagとjob_codeを保持していると検索結果がおかしくなってしまうためリセットしている
    return { ...params, job_type_ids: selectedJobIds, job_codes: [] }
  }

  useEffect(() => {
    // 閉じている間は件数が見えないため、開いている間だけ取得する（最初に開いたときも現在条件で取得される）
    if (!modalIsOpen) return

    const mergedParams = { ...params, job_type_ids: selectedJobIds, job_codes: [] }
    updateCountFromParams(mergedParams)
  }, [modalIsOpen, selectedJobIds, params, updateCountFromParams])

  return (
    <>
      <DefaultButton className="h-10 min-w-30" onClick={toggleModal}>
        職種変更
        <PanelButtonPopupIcon />
      </DefaultButton>
      <SelectModalDialog isOpen={modalIsOpen} onRequestClose={closeModal}>
        <ModalInner>
          <ModalCloseButton onClick={toggleModal} />
          <ModalHeader>
            <ModalTitle>職種を選択</ModalTitle>
          </ModalHeader>
          <ModalListWrapper>
            <BigOccupationList
              bigCategories={bigCategories}
              mappedSmallCategories={mappedSmallCategories}
              selectedJobIds={selectedJobIds}
              changeJobIds={changeJobIds}
            />
          </ModalListWrapper>
          <ModalFooter>
            <SearchControlBox searchUrl={buildDynamicSearchApiUrl(mergedParams())} onClickClearButton={clearState} worksCount={worksCount} />
          </ModalFooter>
        </ModalInner>
      </SelectModalDialog>
    </>
  )
}

export default JobSelectModal
